import { Lock, Unlock } from 'lucide-react';
import { useSecretsLockState } from '@/hooks/useSecretsLockState';
import { InlineLoginForm } from './InlineLoginForm';

interface LockBannerProps {
  /** Custom-Text für den unlocked-State. */
  unlockedMessage?: string;
  /** Custom-Text für den locked-State (oben). */
  lockedTitle?: string;
  /** Test-ID. */
  testId?: string;
}

/**
 * Banner für /settings/secrets (UX-Carry-Over Sprint 4).
 *
 * 3 visuelle Zustände:
 *  - loading  : nichts (vermeidet Flackern)
 *  - unlocked : gruner Banner "Tokens sind editierbar"
 *  - locked   : gelber Banner mit Inline-Login-Form
 *
 * Nutzt `useSecretsLockState` zum Probing + `InlineLoginForm` für das
 * Re-Authentifizieren ohne Logout.
 */
export function LockBanner({
  unlockedMessage = 'Master-Passwort entsperrt - Tokens sind editierbar.',
  lockedTitle = 'Backend-Secrets sind nicht entsperrt.',
  testId = 'lock-banner',
}: LockBannerProps): JSX.Element | null {
  const lock = useSecretsLockState();

  if (lock.state === 'loading') {
    return null;
  }

  if (lock.state === 'unlocked') {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid={testId}
        data-state="unlocked"
        className="flex items-start gap-2 rounded-md border border-success/30 bg-success/5 p-3 text-sm text-success"
      >
        <Unlock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <p>{unlockedMessage}</p>
      </div>
    );
  }

  // locked
  return (
    <div
      role="alert"
      data-testid={testId}
      data-state="locked"
      className="flex flex-col gap-3 rounded-md border border-warning/30 bg-warning/5 p-3 text-sm"
    >
      <div className="flex items-start gap-2 text-warning">
        <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div className="flex-1">
          <p className="font-medium">{lockedTitle}</p>
          <p className="mt-1 text-xs text-warning/90">
            Bitte erneut einloggen - Tokens koennen sonst weder gelesen noch gesetzt
            werden. Status: HTTP {lock.lastStatus ?? '?'}.
          </p>
        </div>
      </div>
      <div className="rounded-md border border-border bg-surface p-3">
        <InlineLoginForm
          hint="Selber User wie beim aktuellen Login - das Master-Passwort entsperrt den KEK-Cache."
          onUnlocked={() => lock.refresh()}
        />
      </div>
    </div>
  );
}
